/** Model catalog — SSOT for model id, provider, context window, capabilities, and pricing (USD/1M tokens). */

export interface ModelPricing {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /**
   * Higher rates for a request whose prompt (input + cache read + cache write)
   * is over `overPromptTokens` — each request is priced on its own (Claude Haiku
   * 5.5, platform.claude.com/docs/en/about-claude/pricing "Long context pricing").
   */
  longPrompt?: { overPromptTokens: number; input: number; output: number; cacheRead: number; cacheWrite: number };
}

export type ModelProviderId = 'anthropic' | 'openai' | 'groq' | 'mistral' | 'gemini' | 'openrouter';

/** Transports that can serve a given context tier. */
export type ContextTransport = 'api' | 'subscription';

/** Opt-in larger context window available on a subset of transports. */
export interface ExtendedContext {
  window: number;
  transports: readonly ContextTransport[];
  /**
   * Transports where this tier bills usage credits rather than plan-included
   * limits — and the request is REJECTED when credits are off ("Usage credits
   * required for 1M context"). Still offered, but never chosen by the routing
   * default: a default that can hard-fail is not a default. Explicit opt-in only.
   */
  creditGated?: readonly ContextTransport[];
}

export interface ModelSpec {
  id: string;
  name: string;
  provider: ModelProviderId;
  /**
   * Optional one-line explainer shown under the model name in the picker —
   * where a version sits in its family, and its context/billing caveats.
   * Omit for entries whose name is self-explanatory.
   */
  description?: string;
  /** Base window — always available on every transport this model supports. */
  contextWindow: number;
  /** Opt-in larger window for transports that can serve it (e.g. 1M on the API). */
  extendedContext?: ExtendedContext;
  /**
   * Transports this model entry is OFFERED on in the picker. Undefined = every
   * transport its provider supports. Every Anthropic entry sets
   * ['api','subscription'] explicitly: `claude --model <id>` accepts any full
   * model name, so API-invokable means subscription-invokable. Narrow this only
   * when a transport genuinely cannot run the model — never to hide one whose
   * window or billing we dislike.
   */
  transports?: readonly ContextTransport[];
  /** Max output tokens per request. */
  maxOutput: number;
  /** Chars/token for budget estimation (varies by tokenizer — Fable≈3, most≈4). */
  charsPerToken: number;
  supportsTools: boolean;
  supportsPromptCaching: boolean;
  supportsVision: boolean;
  /**
   * The API checks this model's thinking blocks against their prefix (preserved
   * thinking: a block is valid only while the system prompt, tools and every
   * earlier message are unchanged). Requests to it carry `block_binding`
   * (`anthropicRequest.ts`). Per platform.claude.com/docs/en/build-with-claude/preserved-thinking
   * (read 2026-10-12): Fable 5.1, Opus 5.5, Sonnet 5.5 and Haiku 5.5.
   */
  preservedThinking?: true;
  /**
   * Effort levels the model accepts, in {@link EFFORT_LEVELS} order. Sent as
   * Anthropic `output_config.effort` / `claude --effort`, OpenAI-compatible
   * `reasoning_effort`, OpenRouter `reasoning.effort`. Sources (read
   * 2026-10-13): platform.claude.com/docs/en/build-with-claude/effort;
   * OpenRouter `GET /api/v1/models` `reasoning.supported_efforts`;
   * console.groq.com/docs/reasoning; ai.google.dev/gemini-api/docs/openai;
   * docs.mistral.ai/capabilities/reasoning. Absent = the model takes none.
   */
  effort?: readonly EffortLevel[];
  /**
   * What the model does when no effort is sent, on the API (`'off'` = no
   * reasoning) — shown as "Default (medium)". Absent = not published.
   */
  defaultEffort?: EffortLevel | 'off';
  /** Claude Code's default where it differs from the API's (code.claude.com/docs/en/model-config). */
  cliDefaultEffort?: EffortLevel;
  /**
   * The model rejects non-default sampling parameters (`temperature`, `top_p`,
   * `top_k`), so they are never sent to it — Claude 4.7 and later (the Opus
   * 5.5, Sonnet 5.5 and Haiku 5.5 migration guides, read 2026-10-13; Fable by
   * its generation — dropping a temperature is the harmless mistake) and
   * OpenAI's GPT-5 reasoning models (no `temperature` in OpenRouter's
   * `supported_parameters`, read 2026-10-13).
   */
  fixedSampling?: true;
  pricing: ModelPricing;
  /** False = catalog-only (retired/preview). Picker and router treat as absent. */
  available?: boolean;
}

// ---------------------------------------------------------------------------
// Reasoning effort
// ---------------------------------------------------------------------------

/** Effort levels accepted by the Anthropic API (`output_config.effort`) and `claude --effort`. */
export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type EffortLevel = (typeof EFFORT_LEVELS)[number];

/** Type guard — narrows an unknown value to the strict EffortLevel union. */
export function isEffortLevel(value: unknown): value is EffortLevel {
  return typeof value === 'string' && (EFFORT_LEVELS as readonly string[]).includes(value);
}

/** Levels of a model that takes every effort but `xhigh` (Opus 4.6, Sonnet 4.6 — effort docs). */
const EFFORT_NO_XHIGH: readonly EffortLevel[] = ['low', 'medium', 'high', 'max'];

/**
 * Error text for a channel that has no model selected. There is deliberately no
 * default model: a hardcoded fallback silently keeps running an old version
 * after newer ones ship, so an unset channel fails loudly and says where to fix it.
 */
export function noModelSelectedMessage(channelLabel: string): string {
  return `No model selected for ${channelLabel} — choose one in Settings → Model.`;
}

/**
 * The catalog provider a backend or route provider runs. The two Claude
 * transports — the subscription plan (`subscription`, CLI) and the Anthropic API
 * (`api`, SDK) — run Anthropic models; every other value IS a provider id.
 *
 * A model id never names its provider: Groq and OpenRouter both serve
 * `openai/gpt-oss-120b`, and Settings accepts any typed id. So every lookup and
 * every route takes the provider the model was chosen on — nothing infers it
 * from the id.
 */
export function providerOfBackend(backend: string): string {
  return backend === 'subscription' || backend === 'api' ? 'anthropic' : backend;
}

/** True only when the catalog marks a model as retired. Unknown ids return false. */
export function isRetired(id: string, provider: string): boolean {
  const spec = getModelSpec(id, provider);
  return spec !== undefined && spec.available === false;
}

/**
 * All models OneStone knows how to price, route, and offer.
 * Anthropic snapshots priced against Anthropic's published table (Sept 2026);
 * Groq and Mistral verified against their live model lists 2026-10-06; other
 * providers as of July 2026 (`npm run verify:model-catalog` checks them live).
 *
 * Every entry is an explicit, versioned model id. Family aliases
 * (`opus`/`sonnet`/`haiku`/`fable`) are deliberately NOT offered: the CLI
 * resolves them by CLI *version*, not by what is newest, so they silently lag.
 * Measured on CLI 2.1.222 — `opus` resolved to Opus 5 (not Opus 5.5) and
 * `fable` to Fable 5 (not 5.1), per code.claude.com/docs/en/model-config, which
 * is the exact "older model at the newer model's price" failure an explicit id
 * cannot have. The CLI still ACCEPTS aliases (see the auth probe in
 * claudeCode/auth.ts); they are just never presented as a selectable model.
 */
export const MODEL_CATALOG: ModelSpec[] = [
  // ── Anthropic ──
  // EVERY snapshot is offered on BOTH transports: `claude --model <id>` accepts
  // any full model name, so anything invokable on the API is invokable on the
  // subscription. Availability is not filtered by context tier — a smaller window
  // is a property to display, never a reason to hide a model the account can run.
  // Newest first, so the top of each family is the current release.
  // Context (code.claude.com/docs/en/model-config, "Extended context", checked
  // 2026-10-03): Fable 5.1/5, Opus 5.5/5 and Sonnet 5.5/5 are 1M-only — no 200K
  // variant. Opus 4.8/4.7/4.6 and Sonnet 4.6 offer a real [200K | 1M] choice,
  // modeled as a 200K base + `extendedContext`; the CLI side of that choice is
  // in claudeCode/argv.ts (`[1m]`) and claudeCode/env.ts (200K hold).
  // Haiku 5.5 is native 1M; Haiku 4.5 is 200K everywhere.
  // ORDER MATTERS: getModelPricing falls back to the first entry whose id is a
  // PREFIX of the requested id, so a more specific id must precede its shorter
  // sibling (claude-opus-5-5 before claude-opus-5, claude-sonnet-5-5 before
  // claude-sonnet-5, claude-fable-5-1 before claude-fable-5) or dated snapshots would price against the wrong model.
  {
    id: 'claude-fable-5-1',
    name: 'Fable 5.1',
    provider: 'anthropic',
    description: 'Current Fable — hardest, longest-running tasks. Native 1M. May bill usage credits.',
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    preservedThinking: true,
    // Cache read is 0.025x input on Fable 5.1 (not the usual 0.1x) — per
    // platform.claude.com/docs/en/about-claude/pricing, checked 2026-09-28.
    defaultEffort: 'high',
    pricing: { input: 10.0, output: 50.0, cacheRead: 0.25, cacheWrite: 12.5 },
  },
  {
    id: 'claude-fable-5',
    name: 'Fable 5',
    provider: 'anthropic',
    description: 'Prior Fable — native 1M. May bill usage credits.',
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    defaultEffort: 'high',
    pricing: { input: 10.0, output: 50.0, cacheRead: 1.0, cacheWrite: 12.5 },
  },
  {
    id: 'claude-opus-5-5',
    name: 'Opus 5.5',
    provider: 'anthropic',
    description: 'Current Opus — top capability for complex reasoning. Native 1M.',
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    preservedThinking: true,
    // Cache read is 0.05x input on Opus 5.5 (not the usual 0.1x) — per the
    // pricing page, checked 2026-09-28.
    defaultEffort: 'medium',
    pricing: { input: 4.0, output: 20.0, cacheRead: 0.2, cacheWrite: 5.0 },
  },
  {
    id: 'claude-opus-5',
    name: 'Opus 5',
    provider: 'anthropic',
    description: 'Prior Opus — native 1M.',
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    defaultEffort: 'high',
    pricing: { input: 5.0, output: 25.0, cacheRead: 0.5, cacheWrite: 6.25 },
  },
  {
    id: 'claude-opus-4-8',
    name: 'Opus 4.8',
    provider: 'anthropic',
    description: 'Older Opus — choose 200K or 1M; 1M is included on every plan.',
    transports: ['api', 'subscription'],
    contextWindow: 200_000,
    extendedContext: { window: 1_000_000, transports: ['api', 'subscription'] },
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    defaultEffort: 'high',
    pricing: { input: 5.0, output: 25.0, cacheRead: 0.5, cacheWrite: 6.25 },
  },
  {
    id: 'claude-opus-4-7',
    name: 'Opus 4.7',
    provider: 'anthropic',
    description: 'Older Opus — choose 200K or 1M; 1M is included on every plan.',
    transports: ['api', 'subscription'],
    contextWindow: 200_000,
    extendedContext: { window: 1_000_000, transports: ['api', 'subscription'] },
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    defaultEffort: 'high',
    cliDefaultEffort: 'xhigh',
    pricing: { input: 5.0, output: 25.0, cacheRead: 0.5, cacheWrite: 6.25 },
  },
  {
    id: 'claude-opus-4-6',
    name: 'Opus 4.6',
    provider: 'anthropic',
    description: 'Older Opus — choose 200K or 1M; 1M needs usage credits on Pro plans.',
    transports: ['api', 'subscription'],
    contextWindow: 200_000,
    // 1M is included on Max/Team/Enterprise but needs usage credits on Pro, and
    // the plan is not visible to OneStone — so the routing default stays at 200K
    // (creditGated) and 1M is an explicit pick, never a default that can fail.
    extendedContext: { window: 1_000_000, transports: ['api', 'subscription'], creditGated: ['subscription'] },
    maxOutput: 128_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_NO_XHIGH,
    defaultEffort: 'high',
    pricing: { input: 5.0, output: 25.0, cacheRead: 0.5, cacheWrite: 6.25 },
  },
  {
    id: 'claude-sonnet-5-5',
    name: 'Sonnet 5.5',
    provider: 'anthropic',
    description: 'Current Sonnet — balanced speed and smarts for daily work. Native 1M.',
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    preservedThinking: true,
    // Cache read is 0.05x input on Sonnet 5.5 ($0.10, half Sonnet 5's) — pricing page
    // and the Sonnet 5.5 migration guide, checked 2026-10-13.
    defaultEffort: 'high',
    cliDefaultEffort: 'medium',
    pricing: { input: 2.0, output: 10.0, cacheRead: 0.1, cacheWrite: 2.5 },
  },
  {
    id: 'claude-sonnet-5',
    name: 'Sonnet 5',
    provider: 'anthropic',
    description: 'Prior Sonnet — native 1M.',
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    // $2/$10 is the standard published rate (pricing page, checked 2026-09-28;
    // the $3/$15 step-up once expected for Sept 1 did not happen).
    defaultEffort: 'high',
    pricing: { input: 2.0, output: 10.0, cacheRead: 0.2, cacheWrite: 2.5 },
  },
  {
    id: 'claude-sonnet-4-6',
    name: 'Sonnet 4.6',
    provider: 'anthropic',
    description: 'Prior Sonnet — 200K base; 1M tier needs usage credits enabled.',
    transports: ['api', 'subscription'],
    contextWindow: 200_000,
    // 1M IS reachable on the subscription via `claude-sonnet-4-6[1m]`; Anthropic
    // gates it on usage credits being enabled (except usage-based Enterprise).
    // Offered rather than hidden — the credit requirement is surfaced in the
    // description so the choice stays the operator's. creditGated keeps the
    // routing default at 200K: `claude-sonnet-4-6[1m]` with credits off returns
    // "Usage credits required for 1M context" (verified via CLI 2.1.283).
    extendedContext: { window: 1_000_000, transports: ['api', 'subscription'], creditGated: ['subscription'] },
    maxOutput: 64_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    effort: EFFORT_NO_XHIGH,
    defaultEffort: 'high',
    pricing: { input: 3.0, output: 15.0, cacheRead: 0.3, cacheWrite: 3.75 },
  },
  {
    id: 'claude-haiku-5-5',
    name: 'Haiku 5.5',
    provider: 'anthropic',
    description: 'Current Haiku — fastest and cheapest. Native 1M; prompts over 100K tokens cost 5×.',
    // Claude Code 2.1.288 runs it but doesn't know it yet (“unrecognized_model”:
    // it reports a 200K window and no cost basis — the cost is then priced from
    // here, streamParser.ts). Checked 2026-10-13.
    transports: ['api', 'subscription'],
    contextWindow: 1_000_000,
    maxOutput: 128_000,
    // The tokenizer of Claude 4.7 and later: ~30% more tokens than Claude 4.6 for the same text (pricing page).
    charsPerToken: 3,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    preservedThinking: true,
    effort: EFFORT_LEVELS,
    fixedSampling: true,
    // Haiku 5.5 overview + pricing page, checked 2026-10-13.
    defaultEffort: 'medium',
    pricing: {
      input: 0.1,
      output: 0.5,
      cacheRead: 0.01,
      cacheWrite: 0.125,
      longPrompt: { overPromptTokens: 100_000, input: 0.5, output: 2.5, cacheRead: 0.05, cacheWrite: 0.625 },
    },
  },
  {
    id: 'claude-haiku-4-5',
    name: 'Haiku 4.5',
    provider: 'anthropic',
    description: 'Prior Haiku — 200K context, no 1M tier, no effort setting.',
    transports: ['api', 'subscription'],
    contextWindow: 200_000,
    maxOutput: 64_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: true,
    supportsVision: true,
    pricing: { input: 1.0, output: 5.0, cacheRead: 0.1, cacheWrite: 1.25 },
  },

  // ── Groq ── (verified 2026-10-06 against Groq's GET /openai/v1/models, which
  // returns each model's window, output cap, features and per-token pricing).
  // Groq's ids carry the vendor (`openai/…`, `qwen/…`) — the same ids OpenRouter
  // serves — so a model is always looked up with its provider.
  {
    id: 'openai/gpt-oss-120b',
    name: 'GPT OSS 120B',
    provider: 'groq',
    contextWindow: 131_072,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: false,
    effort: ['low', 'medium', 'high'],
    pricing: { input: 0.15, output: 0.6, cacheRead: 0.075, cacheWrite: 0 },
  },
  {
    id: 'openai/gpt-oss-20b',
    name: 'GPT OSS 20B',
    provider: 'groq',
    contextWindow: 131_072,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: false,
    effort: ['low', 'medium', 'high'],
    pricing: { input: 0.075, output: 0.3, cacheRead: 0.0375, cacheWrite: 0 },
  },
  {
    id: 'qwen/qwen3.8-27b',
    name: 'Qwen3.8 27B',
    provider: 'groq',
    contextWindow: 131_072,
    maxOutput: 16_384,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: ['low', 'medium', 'high'],
    pricing: { input: 0.8, output: 4.0, cacheRead: 0.4, cacheWrite: 0 },
  },
  // ── Mistral ── (verified 2026-10-06: ids + windows from Mistral's GET
  // /v1/models; prices from docs.mistral.ai/inference/pricing — input, cached
  // input, output per 1M tokens). Mistral publishes no separate output cap;
  // 8,192 is OneStone's default ceiling (Settings → Model max tokens overrides).
  {
    id: 'mistral-medium-latest',
    name: 'Mistral Medium 3.5',
    provider: 'mistral',
    contextWindow: 262_144,
    maxOutput: 8_192,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 1.5, output: 7.5, cacheRead: 0.15, cacheWrite: 0 },
  },
  {
    id: 'mistral-small-latest',
    name: 'Mistral Small 4',
    provider: 'mistral',
    contextWindow: 262_144,
    maxOutput: 8_192,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: ['high'],
    pricing: { input: 0.15, output: 0.6, cacheRead: 0.015, cacheWrite: 0 },
  },
  {
    id: 'codestral-latest',
    name: 'Codestral',
    provider: 'mistral',
    contextWindow: 256_000,
    maxOutput: 8_192,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: false,
    pricing: { input: 0.3, output: 0.9, cacheRead: 0.03, cacheWrite: 0 },
  },

  // ── Google Gemini ──
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    provider: 'gemini',
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: ['low', 'medium', 'high'],
    pricing: { input: 1.25, output: 10.0, cacheRead: 0.315, cacheWrite: 0 },
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    provider: 'gemini',
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: ['low', 'medium', 'high'],
    pricing: { input: 0.15, output: 0.6, cacheRead: 0.0375, cacheWrite: 0 },
  },

  // ── OpenAI ──
  {
    id: 'gpt-5.1',
    name: 'GPT-5.1',
    provider: 'openai',
    contextWindow: 400_000,
    maxOutput: 128_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: ['low', 'medium', 'high'],
    defaultEffort: 'off',
    fixedSampling: true,
    pricing: { input: 0.625, output: 5.0, cacheRead: 0.125, cacheWrite: 0 },
  },
  {
    id: 'gpt-5-mini',
    name: 'GPT-5 Mini',
    provider: 'openai',
    contextWindow: 400_000,
    maxOutput: 128_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: ['low', 'medium', 'high'],
    defaultEffort: 'medium',
    fixedSampling: true,
    pricing: { input: 0.125, output: 1.0, cacheRead: 0.025, cacheWrite: 0 },
  },
  {
    id: 'gpt-4.1',
    name: 'GPT-4.1',
    provider: 'openai',
    contextWindow: 1_000_000,
    maxOutput: 32_768,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 2.0, output: 8.0, cacheRead: 0.5, cacheWrite: 0 },
  },
  {
    id: 'gpt-4.1-mini',
    name: 'GPT-4.1 Mini',
    provider: 'openai',
    contextWindow: 1_000_000,
    maxOutput: 32_768,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 0.4, output: 1.6, cacheRead: 0.1, cacheWrite: 0 },
  },
  // ── OpenRouter (org/model ids — the provider is always the source the model was chosen on) ──
  // Pricing from OpenRouter API, 2026-07-21.
  // Claude via OpenRouter (added 2026-09-22): the compliant way to reach Claude
  // models from the cloud runtime, whose turn runner is openai-compat-only in
  // v1 (Cloud SSOT L3 — subscription OAuth is banned for third-party apps;
  // native Anthropic API-key transport is a follow-up slice). OpenRouter
  // passes Anthropic list pricing through; caching not surfaced on this path.
  {
    id: 'anthropic/claude-sonnet-4.6',
    name: 'Claude Sonnet 4.6 (OpenRouter)',
    provider: 'openrouter',
    contextWindow: 200_000,
    maxOutput: 64_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    effort: EFFORT_NO_XHIGH,
    defaultEffort: 'medium',
    pricing: { input: 3.0, output: 15.0, cacheRead: 0, cacheWrite: 0 },
  },
  {
    id: 'anthropic/claude-haiku-4.5',
    name: 'Claude Haiku 4.5 (OpenRouter)',
    provider: 'openrouter',
    contextWindow: 200_000,
    maxOutput: 64_000,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 1.0, output: 5.0, cacheRead: 0, cacheWrite: 0 },
  },
  {
    id: 'deepseek/deepseek-v4-flash',
    name: 'DeepSeek V4 Flash',
    provider: 'openrouter',
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: false,
    effort: ['high', 'xhigh'],
    defaultEffort: 'high',
    pricing: { input: 0.09, output: 0.18, cacheRead: 0, cacheWrite: 0 },
  },
  {
    id: 'deepseek/deepseek-v4-pro',
    name: 'DeepSeek V4 Pro',
    provider: 'openrouter',
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: false,
    effort: ['high', 'xhigh'],
    defaultEffort: 'high',
    pricing: { input: 0.435, output: 0.87, cacheRead: 0, cacheWrite: 0 },
  },
  {
    id: 'qwen/qwen3.7-plus',
    name: 'Qwen 3.7 Plus',
    provider: 'openrouter',
    contextWindow: 1_000_000,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 0.32, output: 1.28, cacheRead: 0, cacheWrite: 0 },
  },
  {
    id: 'x-ai/grok-4.20',
    name: 'Grok 4.20',
    provider: 'openrouter',
    contextWindow: 2_000_000,
    maxOutput: 131_072,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 1.25, output: 2.5, cacheRead: 0, cacheWrite: 0 },
  },
  {
    id: 'google/gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    provider: 'openrouter',
    contextWindow: 1_048_576,
    maxOutput: 65_536,
    charsPerToken: 4,
    supportsTools: true,
    supportsPromptCaching: false,
    supportsVision: true,
    pricing: { input: 1.25, output: 10, cacheRead: 0, cacheWrite: 0 },
  },
];

const specKey = (provider: string, id: string): string => `${providerOfBackend(provider)}\u0000${id}`;
const BY_KEY = new Map<string, ModelSpec>(MODEL_CATALOG.map((m) => [specKey(m.provider, m.id), m]));

/** The catalog entry for a model on a provider (exact id), or undefined. */
export function getModelSpec(id: string, provider: string): ModelSpec | undefined {
  return BY_KEY.get(specKey(provider, id));
}

/**
 * Exact entry, else the provider's entry the id extends (a dated snapshot of a
 * listed model, e.g. `claude-sonnet-4-6-20260101`). Only that provider's
 * entries are considered.
 */
export function matchModelSpec(id: string, provider: string): ModelSpec | undefined {
  const exact = getModelSpec(id, provider);
  if (exact) return exact;
  const p = providerOfBackend(provider);
  return MODEL_CATALOG.find((m) => m.provider === p && id.startsWith(m.id));
}

/** Pricing for a model on a provider — exact → snapshot of a listed model → null ($0 cost). */
export function getModelPricing(id: string, provider: string): ModelPricing | null {
  return matchModelSpec(id, provider)?.pricing ?? null;
}

/** All models for a given provider. */
export function modelsForProvider(provider: ModelProviderId): ModelSpec[] {
  return MODEL_CATALOG.filter((m) => m.provider === provider);
}

/** Whether the model's extended context is reachable on this transport. */
export function supportsExtendedContext(modelId: string, provider: string, transport: ContextTransport): boolean {
  const spec = getModelSpec(modelId, provider);
  return spec?.extendedContext?.transports.includes(transport) ?? false;
}

/** Whether the extended tier bills usage credits on this transport (explicit opt-in only). */
export function isExtendedContextCreditGated(modelId: string, provider: string, transport: ContextTransport): boolean {
  const spec = getModelSpec(modelId, provider);
  return spec?.extendedContext?.creditGated?.includes(transport) ?? false;
}

/** Effective context window for a model on a provider, tier and transport. Unknown model → 200K. */
export function effectiveContextWindow(
  modelId: string,
  provider: string,
  useExtended: boolean,
  transport: ContextTransport,
): number {
  const spec = getModelSpec(modelId, provider);
  const base = spec?.contextWindow ?? 200_000;
  if (!useExtended) return base;
  const ext = spec?.extendedContext;
  if (!ext || !ext.transports.includes(transport)) return base;
  return ext.window;
}

/**
 * Effort levels a model accepts (the catalog's). Empty = none, including a
 * model the catalog doesn't know. What is sent for a chosen level is
 * `resolveModelParams` (`modelParams.ts`).
 */
export function effortLevelsFor(modelId: string, provider: string): readonly EffortLevel[] {
  return (modelId && matchModelSpec(modelId, provider)?.effort) || [];
}

/** Whether a model accepts any reasoning-effort parameter at all. */
export function supportsEffort(modelId: string, provider: string): boolean {
  return effortLevelsFor(modelId, provider).length > 0;
}

/** Default for models not in the catalog (Ollama, OpenRouter). */
export const DEFAULT_CHARS_PER_TOKEN = 4;
const DEFAULT_MAX_OUTPUT = 8_192;

/** Chars/token for a model on a provider (exact → snapshot → default 4). */
export function charsPerTokenFor(id: string, provider: string): number {
  return matchModelSpec(id, provider)?.charsPerToken ?? DEFAULT_CHARS_PER_TOKEN;
}

/**
 * Tokens an image counts as when estimating: the most a Claude model spends on
 * one (4,784 visual tokens, the high-resolution tier's cap —
 * platform.claude.com/docs/en/build-with-claude/vision, checked 2026-10-13).
 * Its base64 text says nothing about its cost.
 */
export const IMAGE_TOKEN_ESTIMATE = 4_784;

/**
 * Estimated tokens in a list of messages at `charsPerToken`: a text block
 * counts its text, an image {@link IMAGE_TOKEN_ESTIMATE}, any other block its
 * JSON. An estimate for budgeting only — the provider's measured prompt count
 * is the truth.
 */
export function estimateTokens(
  messages: ReadonlyArray<{ content: string | ReadonlyArray<{ type: string; text?: unknown }> }>,
  charsPerToken: number,
): number {
  let chars = 0;
  for (const m of messages) {
    if (typeof m.content === 'string') {
      chars += m.content.length;
      continue;
    }
    for (const b of m.content) {
      if (b.type === 'image') chars += IMAGE_TOKEN_ESTIMATE * charsPerToken;
      else chars += b.type === 'text' && typeof b.text === 'string' ? b.text.length : JSON.stringify(b).length;
    }
  }
  return Math.ceil(chars / charsPerToken);
}

/** Max output tokens for a model on a provider (exact → snapshot → 8192). */
export function maxOutputFor(id: string, provider: string): number {
  return matchModelSpec(id, provider)?.maxOutput ?? DEFAULT_MAX_OUTPUT;
}

/**
 * Bounded default ceiling for metered/preflight providers (see
 * isMeteredPreflightProvider) — sized to typical turns, not the model's
 * theoretical max. Matches OpenRouter's own documented guidance for agent
 * tools ("set max_tokens... a generous 4096 or 8192"); confirmed via
 * openrouter.ai support docs + reservation-math case study, 2026-07-30.
 */
const METERED_DEFAULT_MAX_OUTPUT = 8_192;

/**
 * Whether a model is served through a metered, prepaid, pay-per-token API
 * that preflight-checks the REQUESTED max_tokens ceiling against account
 * balance before generating a single token (OpenRouter's documented
 * behavior — openrouter.ai/docs/api_reference/limits). Subscription
 * transports (Claude Pro/Max via CLI) and direct provider API keys bill
 * post-hoc with no such reservation, so they're free to request the
 * model's full ceiling with zero cost/failure risk.
 */
function isMeteredPreflightProvider(provider: string): boolean {
  return provider === 'openrouter';
}

/**
 * Default max_tokens ceiling for a turn when no explicit override is set —
 * single source of truth shared by chat, sub-agents, and routines.
 *
 * Metered/preflight providers default to a bounded, task-sized ceiling
 * instead of the model's full capability max: requesting the full ceiling
 * reserves against the WORST CASE the model could generate, not actual
 * usage, so a modest-but-real balance trips a 402 even when real spend
 * would be pennies. Subscription/non-metered transports are unaffected —
 * they keep requesting the model's real ceiling (no reservation cost).
 * A caller that genuinely needs more sets an explicit per-channel maxTokens
 * override (Settings → Model), which always wins over this default.
 */
export function defaultMaxOutputFor(modelId: string, provider: string, modelWindow: number, floor = 1024): number {
  const catalogCeiling = maxOutputFor(modelId, provider);
  const ceiling = isMeteredPreflightProvider(provider)
    ? Math.min(catalogCeiling, METERED_DEFAULT_MAX_OUTPUT)
    : catalogCeiling;
  return Math.max(floor, Math.min(ceiling, Math.floor(modelWindow / 4)));
}

/**
 * Provider-side fallback max_tokens when a caller supplied no explicit ceiling.
 * SINGLE SOURCE OF TRUTH shared by both provider implementations (anthropic +
 * openai-compat) so their defaults cannot diverge — historically anthropic used
 * a model-aware value while openai-compat hardcoded 16_384 (wave-1b audit).
 *
 * In practice every real caller (chat-budget, sub-agents, scheduler, summarize)
 * passes an explicit maxTokens, so this is defense-in-depth for direct provider
 * calls, not the primary path. Model-aware + metered-aware (OpenRouter caps low
 * to avoid balance-reservation 402s); derives a conservative window from the
 * catalog (200K default for unknown models).
 */
export function fallbackMaxOutputFor(modelId: string, provider: string): number {
  return defaultMaxOutputFor(modelId, provider, effectiveContextWindow(modelId, provider, false, 'api'));
}

// ---------------------------------------------------------------------------
// Vision capability
// ---------------------------------------------------------------------------

/** Ollama model id patterns with vision support (matched case-insensitively). */
const OLLAMA_VISION_PATTERNS: readonly string[] = [
  'llava',
  'bakllava',
  'llama3.2-vision',
  'llama4-vision',
  'qwen-vl',
  'qwen2-vl',
  'qwen2.5-vl',
  'minicpm-v',
  'moondream',
  'gemma3', // Gemma 3 multimodal (text + vision)
  'pixtral',
  'phi-3.5-vision',
  'phi-4-multimodal',
  'cogvlm',
  'internvl',
];

/** Whether a model on a provider accepts image input. Catalog → Ollama pattern → false. */
export function modelSupportsVision(modelId: string, provider: string): boolean {
  if (!modelId) return false;
  const spec = matchModelSpec(modelId, provider);
  if (spec) return spec.supportsVision;
  if (provider === 'ollama') {
    const lower = modelId.toLowerCase();
    return OLLAMA_VISION_PATTERNS.some((p) => lower.includes(p));
  }
  return false;
}
